import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { doc, getDoc, setDoc, deleteDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { checkSeatLimit } from '../lib/featureGating';
import { accountService } from '../lib/account/accountService';

export default function JoinWorkspace() {
  const { workspaceId, inviteId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    async function processInvite() {
      if (!user || !workspaceId || !inviteId) {
        setError("Invalid link or missing authentication.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        // We bypass some specific strict rules momentarily or rely on the user email matching.
        // If this fetch fails, it generally means the token doesn't match the invite's email OR it was already deleted.
        const inviteRef = doc(db, 'workspaces', workspaceId, 'invitations', inviteId);
        const inviteSnap = await getDoc(inviteRef);

        if (!inviteSnap.exists()) {
          setError("This invitation link has expired or is invalid.");
          setLoading(false);
          return;
        }

        const inviteData = inviteSnap.data();

        // Security check
        if (inviteData.email.toLowerCase() !== user.email?.toLowerCase()) {
          setError(`This invitation was sent to ${inviteData.email}. Please log in with that account to claim it.`);
          setLoading(false);
          return;
        }

        // Seat limit check via workspace owner account
        const wsSnap = await getDoc(doc(db, 'workspaces', workspaceId));
        const ownerId = wsSnap.exists()
          ? (wsSnap.data().ownerId as string) ?? workspaceId
          : workspaceId;
        const ownerAccount = await accountService.getAccountForUser(ownerId);
        if (ownerAccount) {
          const seatResult = await checkSeatLimit(ownerAccount.id);
          if (!seatResult.allowed) {
            setError(seatResult.message ?? 'This workspace has reached its seat limit.');
            setLoading(false);
            return;
          }
        }

        // Apply Invite
        // 1. Create Member Document
        const memberRef = doc(db, 'workspaces', workspaceId, 'members', user.uid);
        await setDoc(memberRef, {
          workspaceId,
          userId: user.uid,
          inviteId,
          role: inviteData.role,
          status: 'active',
          joinedAt: serverTimestamp(),
        });

        // 2. Set as User's Active Workspace
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, {
          currentWorkspaceId: workspaceId
        });

        // 3. Delete Invitation
        await deleteDoc(inviteRef);

        setSuccess(true);
      } catch (err: any) {
        console.error(err);
        if (err.code === 'permission-denied') {
           setError("You do not have permission to join this workspace. Ensure you are signed in with the correct email.");
        } else {
           setError("An unexpected error occurred while processing the invitation.");
        }
      } finally {
        setLoading(false);
      }
    }

    processInvite();
  }, [user, workspaceId, inviteId]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-[#fafafa]">
        <Loader2 className="w-12 h-12 text-primary animate-spin mb-4" />
        <h2 className="text-xl font-bold text-[#0F172A]">Applying Invitation...</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-[#fafafa] p-4">
        <div className="max-w-md w-full bg-white p-8 rounded-3xl border border-red-200 text-center shadow-sm">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Invalid Invitation</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full bg-gray-100 text-gray-700 font-bold py-3 rounded-xl hover:bg-gray-200 transition"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-[#fafafa] p-4">
        <div className="max-w-md w-full bg-white p-8 rounded-3xl border border-green-200 text-center shadow-sm">
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome to the Team!</h2>
          <p className="text-gray-600 mb-6">You have successfully joined the workspace.</p>
          <button
            onClick={() => {
              // Note: Forced reload to ensure the root AuthContext pulls down the new Workspace ID completely from scratch.
              window.location.href = appUrl('/dashboard');
            }}
            className="btn-primary w-full py-3 font-bold"
          >
            Enter Workspace
          </button>
        </div>
      </div>
    );
  }

  return <Navigate to="/dashboard" replace />;
}
